import React from 'react';
import { BoxIcon } from 'lucide-react';
interface AVButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'active' | 'danger' | 'warning' | 'selected' | 'ghost';
  icon?: BoxIcon;
  label?: string;
  subLabel?: string;
  active?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}
export function AVButton({
  variant = 'default',
  icon: Icon,
  label,
  subLabel,
  active = false,
  fullWidth = false,
  size = 'md',
  className = '',
  children,
  ...props
}: AVButtonProps) {
  // Base styles for AV aesthetic: Flat, high contrast, no rounded corners (or minimal)
  const baseStyles = 'relative flex flex-col items-center justify-center transition-all duration-150 border-2 font-medium tracking-wide active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none';
  const sizeStyles = {
    sm: 'h-12 px-3 text-sm',
    md: 'h-16 px-4 text-base',
    lg: 'h-24 px-6 text-lg',
    xl: 'h-32 px-8 text-xl' // For mode buttons
  };
  // Color mapping based on AV standards
  // Default: Dark Gray surface, Light Gray border
  // Active (Green): Operation active / ON
  // Danger (Red): Mute / Stop / Panic
  // Selected (Blue): Current source / Page
  // Warning (Amber): Caution
  const getVariantStyles = () => {
    if (active && variant === 'default') return 'bg-green-600 border-green-400 text-white shadow-[0_0_10px_rgba(22,163,74,0.3)]';
    switch (variant) {
      case 'active':
        return 'bg-green-600 border-green-400 text-white shadow-[0_0_15px_rgba(22,163,74,0.4)]';
      case 'danger':
        return active ? 'bg-red-600 border-red-400 text-white shadow-[0_0_15px_rgba(220,38,38,0.4)]' : 'bg-gray-800 border-red-900/50 text-red-400 hover:bg-red-900/20 hover:border-red-500';
      case 'warning':
        return active ? 'bg-amber-600 border-amber-400 text-white' : 'bg-gray-800 border-amber-900/50 text-amber-400 hover:bg-amber-900/20';
      case 'selected':
        return 'bg-blue-600 border-blue-400 text-white shadow-[0_0_15px_rgba(37,99,235,0.4)]';
      case 'ghost':
        return 'bg-transparent border-transparent text-gray-400 hover:text-white hover:bg-white/5';
      default:
        return 'bg-neutral-800 border-neutral-700 text-gray-200 hover:bg-neutral-700 hover:border-neutral-500 hover:text-white';
    }
  };
  return <button className={`
        ${baseStyles} 
        ${sizeStyles[size]} 
        ${getVariantStyles()} 
        ${fullWidth ? 'w-full' : ''} 
        ${className}
        rounded-md
      `} {...props} data-id="element-303">
      {Icon && <Icon className={`mb-1 ${label ? 'h-6 w-6' : 'h-8 w-8'}`} data-id="element-304" />}
      {label && <span className="font-semibold uppercase" data-id="element-305">{label}</span>}
      {subLabel && <span className="text-xs opacity-80 mt-1 font-normal normal-case" data-id="element-306">
          {subLabel}
        </span>}
      {children}
    </button>;
}